import {
  ArrowDown,
  ArrowUp,
  Clipboard,
  Copy,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  FilePlus,
  FolderInput,
  FolderOpen,
  FolderPlus,
  History,
  Link2,
  MessageSquare,
  MessageSquarePlus,
  MonitorUp,
  PanelLeftOpen,
  Pencil,
  Play,
  Plus,
  Settings,
  Share2,
  Star,
  ToggleLeft,
  ToggleRight,
  Trash2,
  Undo2,
  Upload,
  Users,
  type LucideIcon,
} from "lucide-react";
import type {
  ActionLabelContext,
  ObjectActionDefinition,
  ObjectActionGroupKey,
  ObjectActionId,
} from "./types";

export const OBJECT_ACTION_GROUPS: Record<
  ObjectActionGroupKey,
  readonly ObjectActionId[]
> = {
  navigation: ["open", "open-in-new-tab", "locate-on-canvas", "edit", "manage"],
  organization: [
    "rename",
    "move",
    "duplicate",
    "copy",
    "new-folder",
    "new-document",
    "new-canvas",
    "new-chat",
    "copy-to-workspace",
    "wrap-in-section",
    "add-to-my-skills",
    "set-auto",
    "set-manual",
  ],
  // Share / Publish / Copy URL form one subsection — Copy URL surfaces only
  // once a file is published, so it reads as part of the sharing cluster.
  sharing: ["share", "publish", "copy-url", "add-members", "edit-workspace"],
  favorites: ["star", "unstar"],
  file: ["download", "upload-files", "unpublish", "version-history"],
  "canvas-order": ["bring-to-front", "send-to-back"],
  collaboration: ["chat-about", "execute-action-item"],
  destructive: [
    "remove-from-canvas",
    "show-on-canvas",
    "delete",
    "delete-wrapper-only",
    "restore",
    // Bulk archive sits with the other destructive rows so menus group it
    // with Hide/Delete instead of giving it a lone trailing subsection.
    "bulk-delete",
  ],
  bulk: ["bulk-move"],
};

// "Deleting" is archival — everything restorable lands on the Archived page —
// so resources with an archive home say "Archive". Plain canvas nodes are
// genuinely destroyed (no restore), so they keep "Delete". A file *entity*
// node is the exception among nodes: deleting it archives the backing file
// record, so it says "Archive" like every other file surface.
function trashLabel(ctx: ActionLabelContext): string {
  if (
    ctx.kind.type === "canvas" ||
    ctx.kind.type === "chat" ||
    ctx.kind.type === "skill" ||
    ctx.kind.type === "workspace" ||
    ctx.kind.type === "file-record" ||
    ctx.kind.type === "resource-folder" ||
    (ctx.kind.type === "canvas-node" && ctx.kind.entityBase === "file-record")
  ) {
    return "Archive";
  }
  return "Delete";
}

function publishLabel(ctx: ActionLabelContext): string {
  return ctx.kind.type === "skill" ? "Publish to organization" : "Publish";
}

function downloadLabel(ctx: ActionLabelContext): string {
  // Skills bundle multiple files, so the skill surfaces download a zip.
  return ctx.kind.type === "skill" ? "Download zip" : "Download";
}

export const OBJECT_ACTION_CATALOG: Record<
  ObjectActionId,
  ObjectActionDefinition
> = {
  open: {
    id: "open",
    label: "Open",
    // Folders open by navigating into them, not in the right pane — show the
    // folder glyph there (file folders and canvas/chat resource folders alike).
    icon: (ctx: ActionLabelContext) =>
      (ctx.kind.type === "file-record" && ctx.kind.isFolder) ||
      ctx.kind.type === "resource-folder"
        ? FolderOpen
        : PanelLeftOpen,
    group: "navigation",
  },
  "open-in-new-tab": {
    id: "open-in-new-tab",
    label: "Open in new tab",
    icon: ExternalLink,
    group: "navigation",
  },
  "locate-on-canvas": {
    id: "locate-on-canvas",
    label: "Locate on canvas",
    icon: ExternalLink,
    group: "navigation",
  },
  edit: {
    id: "edit",
    label: "Edit",
    icon: Pencil,
    group: "navigation",
  },
  manage: {
    id: "manage",
    label: "Manage",
    icon: Settings,
    group: "navigation",
  },
  rename: {
    id: "rename",
    label: "Rename",
    icon: Pencil,
    group: "organization",
  },
  move: {
    id: "move",
    label: "Move to",
    icon: FolderInput,
    group: "organization",
  },
  duplicate: {
    id: "duplicate",
    label: "Duplicate",
    icon: Copy,
    group: "organization",
    shortcut: "⌘D",
  },
  copy: {
    id: "copy",
    label: "Copy",
    icon: Clipboard,
    group: "organization",
    shortcut: "⌘C",
  },
  "new-folder": {
    id: "new-folder",
    label: "New folder",
    icon: FolderPlus,
    group: "organization",
  },
  "new-document": {
    id: "new-document",
    label: "New document",
    icon: FilePlus,
    group: "organization",
  },
  "new-canvas": {
    id: "new-canvas",
    label: "New Canvas",
    icon: Plus,
    group: "organization",
  },
  "new-chat": {
    id: "new-chat",
    label: "New chat",
    icon: MessageSquarePlus,
    group: "organization",
  },
  "upload-files": {
    id: "upload-files",
    label: "Upload files",
    icon: Upload,
    group: "file",
  },
  "copy-to-workspace": {
    id: "copy-to-workspace",
    label: "Copy to workspace",
    icon: FolderInput,
    group: "organization",
  },
  "copy-url": {
    id: "copy-url",
    label: "Copy URL",
    icon: Link2,
    group: "sharing",
  },
  "wrap-in-section": {
    id: "wrap-in-section",
    label: "Wrap in section",
    icon: FolderInput,
    group: "organization",
  },
  // Hide is the non-destructive counterpart to delete: the node (and, for a
  // file entity, the file's canvas membership) stays intact — the canvas just
  // stops rendering it. The rail lists hidden nodes with "Show on canvas".
  "remove-from-canvas": {
    id: "remove-from-canvas",
    label: "Hide on canvas",
    icon: EyeOff,
    group: "destructive",
  },
  "show-on-canvas": {
    id: "show-on-canvas",
    label: "Show on canvas",
    icon: Eye,
    group: "destructive",
  },
  "add-to-my-skills": {
    id: "add-to-my-skills",
    label: "Add to my skills",
    icon: Plus,
    group: "organization",
  },
  "set-auto": {
    id: "set-auto",
    label: "Set to Auto",
    icon: ToggleRight,
    group: "organization",
  },
  "set-manual": {
    id: "set-manual",
    label: "Set to Manual",
    icon: ToggleLeft,
    group: "organization",
  },
  share: {
    id: "share",
    label: "Share",
    icon: Share2,
    group: "sharing",
  },
  "add-members": {
    id: "add-members",
    label: "Add members",
    icon: Users,
    group: "sharing",
  },
  "edit-workspace": {
    id: "edit-workspace",
    label: "Edit workspace",
    icon: Pencil,
    group: "sharing",
  },
  star: {
    id: "star",
    label: "Star",
    icon: Star,
    group: "favorites",
  },
  unstar: {
    id: "unstar",
    label: "Unstar",
    icon: Star,
    group: "favorites",
  },
  download: {
    id: "download",
    label: downloadLabel,
    icon: Download,
    group: "file",
    /** Export formats live in fileRecordExportCatalog.ts; expanded via variantSource. */
  },
  publish: {
    id: "publish",
    label: publishLabel,
    icon: MonitorUp,
    group: "sharing",
  },
  unpublish: {
    id: "unpublish",
    label: "Unpublish",
    icon: Trash2,
    group: "file",
    destructive: true,
  },
  "version-history": {
    id: "version-history",
    label: "Version history",
    icon: History,
    group: "file",
  },
  "bring-to-front": {
    id: "bring-to-front",
    label: "Bring to front",
    icon: ArrowUp,
    group: "canvas-order",
    shortcut: "⌘]",
  },
  "send-to-back": {
    id: "send-to-back",
    label: "Send to back",
    icon: ArrowDown,
    group: "canvas-order",
    shortcut: "⌘[",
  },
  "chat-about": {
    id: "chat-about",
    label: "Chat about this",
    icon: MessageSquare,
    group: "collaboration",
  },
  "execute-action-item": {
    id: "execute-action-item",
    label: "Execute",
    icon: Play,
    group: "collaboration",
  },
  delete: {
    id: "delete",
    label: trashLabel,
    icon: Trash2,
    group: "destructive",
    destructive: true,
    trashPolicy: "confirm",
  },
  "delete-wrapper-only": {
    id: "delete-wrapper-only",
    label: "Delete wrapper only",
    icon: Trash2,
    group: "destructive",
    destructive: true,
  },
  restore: {
    id: "restore",
    label: "Restore",
    icon: Undo2,
    group: "destructive",
  },
  "bulk-move": {
    id: "bulk-move",
    label: "Move",
    icon: FolderInput,
    group: "bulk",
  },
  "bulk-delete": {
    id: "bulk-delete",
    label: "Archive",
    icon: Trash2,
    group: "destructive",
    destructive: true,
  },
};

export function resolveActionLabel(
  definition: ObjectActionDefinition,
  ctx: ActionLabelContext,
): string {
  return typeof definition.label === "function"
    ? definition.label(ctx)
    : definition.label;
}

export function resolveActionIcon(
  definition: ObjectActionDefinition,
  ctx: ActionLabelContext,
): LucideIcon {
  const icon = definition.icon;
  // Lucide icons are forwardRef exotic components (objects carrying $$typeof),
  // so a plain function here can only be a context resolver.
  if (typeof icon === "function" && !("$$typeof" in icon)) {
    return (icon as (ctx: ActionLabelContext) => LucideIcon)(ctx);
  }
  return icon as LucideIcon;
}

export function getObjectActionDefinition(
  id: ObjectActionId,
): ObjectActionDefinition {
  return OBJECT_ACTION_CATALOG[id];
}
